import React from 'react';
import { useUserDetialsContext } from '../context/user-details';
import PageContainer from '../components/UI/PageContainer';

const HiddenTestPage = () => {
    const { userDetails } = useUserDetialsContext();
    // const userDetails = { email: 'wyattelliott27@gmail.com' };
    const authorizedEmails = ['wyattelliott27@gmail.com', 'michael@fullfr.me'];

    // Debugging
    console.log('HiddenTestPage: userDetails', userDetails);

    // UI State
    const [file, setFile] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isFullWidth, setIsFullWidth] = React.useState(false);
    const [analysis, setAnalysis] = React.useState({
        title: '',
        logline: '',
        shortSummary: '',
        longSummary: '',
        parsedText: '',
        synopsis: '',
        scenes: [],
        debugFinalReport: null
    });

    // Log Viewer State (Moved up to avoid conditional hook error)
    const [logs, setLogs] = React.useState([]);
    const [isLogModalOpen, setIsLogModalOpen] = React.useState(false);
    const [currentLogSubmissionId, setCurrentLogSubmissionId] = React.useState(null);
    const [loadingLogs, setLoadingLogs] = React.useState(false);
    // Link Copy State
    const [isCopying, setIsCopying] = React.useState(false);

    const fetchLogs = async (submissionId) => {
        if (!userDetails?.tokenId) return;
        setLoadingLogs(true);
        setCurrentLogSubmissionId(submissionId);
        setIsLogModalOpen(true);
        setLogs([]); // Clear previous

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v2/logs/${submissionId}/`, {
                 headers: {
                    'Authorization': `Bearer ${userDetails.tokenId}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setLogs(data.logs || []);
            } else {
                console.error("Failed to fetch logs");
            }
        } catch (e) {
            console.error("Error fetching logs:", e);
        } finally {
            setLoadingLogs(false);
        }
    };

    // Submissions List State
    const [submissions, setSubmissions] = React.useState([]);
    const [isLoadingList, setIsLoadingList] = React.useState(false);

    // Fetch Submissions List
    const fetchSubmissions = React.useCallback(async () => {
        if (!userDetails?.tokenId) return;
        setIsLoadingList(true);
        try {
            // Using existing endpoint: submission/list/
            // Filter only for v2 coverage (from Lab page)
            const filters = JSON.stringify({ is_v2_coverage: true });
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}submission/list/?page_size=20&filters=${encodeURIComponent(filters)}`, {
                headers: {
                    'Authorization': `Bearer ${userDetails.tokenId}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                // API returns { data: [...], is_end: bool, next_page: int }
                setSubmissions(data.data || []); 
            } else {
                console.error("Failed to fetch submissions list");
            }
        } catch (e) {
            console.error("Error fetching submissions:", e);
        } finally {
            setIsLoadingList(false);
        }
    }, [userDetails?.tokenId]);

    // Load initial list on mount (if user is authorized)
    React.useEffect(() => {
        if (userDetails?.tokenId && authorizedEmails.includes(userDetails.email)) {
            fetchSubmissions();
        }
    }, [userDetails?.tokenId, fetchSubmissions, userDetails?.email]); // Added dependencies

    const loadSubmission = (id) => {
        pollSubmission(id);
        // Scroll to top
        window.scrollTo({ top: 300, behavior: 'smooth' });
    };


    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Polling Logic (Re-usable)
    const pollSubmission = React.useCallback(async (submissionId) => {
        if (!userDetails?.tokenId) return;

        setIsLoading(true);
        setAnalysis(prev => ({ ...prev, parsedText: `Resuming/Polling Job (ID: ${submissionId})...` }));
        
        // Save to Persistence
        localStorage.setItem('activeSubmissionId', submissionId);

        let attempts = 0;
        const maxAttempts = 1200; 

        // Initial check immediately
        const checkStatus = async () => {
            attempts++;
            try {
                const statusRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}submission/?id=${submissionId}&json=true`, {
                    headers: {
                        'Authorization': `Bearer ${userDetails.tokenId}`
                    }
                });
                
                if (!statusRes.ok) {
                    console.error("Polling error:", statusRes.statusText);
                    return false; 
                }

                const statusData = await statusRes.json();
                
                const status = statusData.status?.toLowerCase();
                
                if (status === 'completed') {
                    setIsLoading(false);
                    setAnalysis(prev => ({
                        ...prev,
                        parsedText: statusData.parsed_text || "Text available in debug",
                        synopsis: statusData.long_summary || statusData.synopsis,
                        longSummary: statusData.long_summary,
                        shortSummary: statusData.short_summary,
                        logline: statusData.logline,
                        scenes: statusData.scenes || [],
                        title: statusData.title || statusData.filename,
                        debugChunkResults: statusData.debug_chunk_results,
                        debugFinalReport: statusData.debug_final_report,
                    }));
                    return true; // Stop polling
                } else if (status === 'failed') {
                    setIsLoading(false);
                    setAnalysis(prev => ({ ...prev, parsedText: `Analysis Failed: ${statusData.error_message || "Unknown error"}` }));
                    return true; // Stop polling
                } else {
                     setAnalysis(prev => ({ ...prev, parsedText: `Processing... (${attempts * 3}s elapsed)` }));
                     return false; // Continue polling
                }

            } catch (e) {
                console.error("Polling exception:", e);
                return false;
            }
        };

        // Run once immediately
        const finished = await checkStatus();
        if (finished) return;

        // Start Loop
        const pollInterval = setInterval(async () => {
            const isDone = await checkStatus();
            if (isDone || attempts >= maxAttempts) {
                clearInterval(pollInterval);
                if (attempts >= maxAttempts) {
                     setIsLoading(false);
                     alert("Timeout waiting for analysis.");
                }
            }
        }, 3000);
        
        // Cleanup function for useEffect (if component unmounts)
        return () => clearInterval(pollInterval);

    }, [userDetails?.tokenId]);

    // Resume from LocalStorage on Mount
    React.useEffect(() => {
        const savedId = localStorage.getItem('activeSubmissionId');
        if (savedId && userDetails?.tokenId) {
            console.log("Found active submission in storage, resuming:", savedId);
            pollSubmission(savedId);
        }
    }, [userDetails?.tokenId, pollSubmission]);

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setAnalysis(prev => ({ ...prev, parsedText: 'Uploading and Starting Analysis...' }));
        setIsLoading(true);

        try {
            console.log('Sending Analysis Request with Token:', userDetails?.tokenId);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v2/analyze/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            const startData = await response.json();
            const submissionId = startData.submission_id;
            console.log("Job Started. Submission ID:", submissionId);
            
            // Start Polling
            pollSubmission(submissionId);

        } catch (e) {
            setIsLoading(false);
            console.error("Upload Error:", e);
            alert(`Error: ${e.message}`);
        }
    };

    const clearAnalysis = () => {
        setAnalysis({
            title: '',
            logline: '',
            shortSummary: '',
            longSummary: '',
            parsedText: '',
            synopsis: '',
            scenes: [],
            debugChunkResults: null,
            debugFinalReport: null
        });
        setFile(null);
        localStorage.removeItem('activeSubmissionId');
        window.location.reload(); // Quick reset
    };

    const [manualId, setManualId] = React.useState('');
    const handleManualRecover = () => {
        if (!manualId) return;
        pollSubmission(manualId.trim());
    };

    // Loading State
    if (!userDetails || !userDetails.email) {
         return (
             <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-2xl font-bold mb-4">Verifying Access...</h1>
                    <p>Current Email: {userDetails?.email || 'Loading...'}</p>
                </div>
            </PageContainer>
        );
    }

    if (!authorizedEmails.includes(userDetails.email)) {
         return (
             <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-4xl font-bold mb-4">404 - Page Not Found (Access Denied)</h1>
                    <p>The page you are looking for does not exist for user: {userDetails.email}</p>
                </div>
            </PageContainer>
        );
    }




    const handleCopyLogs = async () => {
        if (!logs || logs.length === 0) return;
        
        try {
            await navigator.clipboard.writeText(JSON.stringify(logs, null, 2));
            setIsCopying(true);
            setTimeout(() => setIsCopying(false), 2000);
        } catch (err) {
            console.error('Failed to copy logs:', err);
        }
    };

    // Close Modal
    const closeLogModal = () => {
        setIsLogModalOpen(false);
        setLogs([]);
        setIsCopying(false);
    };

    return (
        <PageContainer>
             {/* Log Viewer Modal */}
             {isLogModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={closeLogModal}>
                    <div className="bg-gray-900 border border-gray-700 w-full max-w-4xl h-[80vh] rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-gray-700 bg-gray-800">
                            <h3 className="text-lg font-bold text-gray-200">Processing Logs <span className="text-gray-500 text-sm font-mono ml-2">ID: {currentLogSubmissionId}</span></h3>
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={handleCopyLogs}
                                    className={`text-xs px-3 py-1 rounded border transition-colors flex items-center gap-1 ${
                                        isCopying 
                                            ? 'bg-green-900/50 text-green-400 border-green-800' 
                                            : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600'
                                    }`}
                                    disabled={logs.length === 0}
                                >
                                    {isCopying ? '✓ Copied!' : '📋 Copy Json'}
                                </button>
                                <button onClick={closeLogModal} className="text-gray-400 hover:text-white p-1">✕</button>
                            </div>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {loadingLogs ? (
                                <div className="text-center text-gray-500 py-10">Loading logs protocol...</div>
                            ) : logs.length === 0 ? (
                                <div className="text-center text-gray-500 py-10">No logs found for this submission.</div>
                            ) : (
                                logs.map((log, idx) => (
                                    <div key={idx} className={`border rounded-lg p-3 transition-all ${
                                        log.status === 'FAILURE' ? 'border-red-900 bg-red-900/10' : 
                                        log.status === 'SUCCESS' ? 'border-green-900 bg-green-900/10' : 'border-gray-700 bg-gray-800'
                                    }`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                                    log.status === 'FAILURE' ? 'bg-red-500 text-white' : 
                                                    log.status === 'SUCCESS' ? 'bg-green-500 text-black' : 'bg-gray-500 text-white'
                                                }`}>{log.status}</span>
                                                <span className="font-mono text-sm text-blue-300 font-semibold">{log.step_name}</span>
                                                <span className="text-xs text-gray-500">Run: {log.run_id?.substring(0,8)}...</span>
                                            </div>
                                            <span className="text-xs text-gray-500">{new Date(log.created_at).toLocaleTimeString()}</span>
                                        </div>
                                        
                                        {/* Error Message */}
                                        {log.error_message && (
                                            <div className="mt-2 text-xs text-red-300 bg-red-900/20 p-2 rounded border border-red-900 font-mono whitespace-pre-wrap">
                                                <strong>Error:</strong> {log.error_message}
                                            </div>
                                        )}
                                        
                                        {/* Details Accordion */}
                                        <details className="mt-2">
                                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 select-none">Show Details / Output</summary>
                                            <div className="mt-2 space-y-2 pl-2 border-l border-gray-700">
                                                {log.input_metadata && (
                                                     <div className="text-xs text-gray-400 font-mono">
                                                        <span className="text-purple-400">Input:</span> {JSON.stringify(log.input_metadata)}
                                                     </div>
                                                )}
                                                {log.usage && (
                                                     <div className="text-xs text-gray-400 font-mono">
                                                        <span className="text-yellow-400">Tokens:</span> {JSON.stringify(log.usage)}
                                                     </div>
                                                )}
                                                {log.raw_output && (
                                                    <div className="mt-2">
                                                        <div className="text-xs text-gray-500 mb-1">Raw Output:</div>
                                                        <div className="bg-black p-2 rounded border border-gray-800 font-mono text-[10px] text-green-300 whitespace-pre-wrap max-h-60 overflow-y-auto">
                                                            {log.raw_output}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </details>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className={`flex flex-col items-center justify-center p-8 text-white w-full mx-auto transition-all duration-300 ${isFullWidth ? 'max-w-full px-12' : 'max-w-5xl'}`}>
                <div className="flex flex-col md:flex-row justify-end w-full mb-4 gap-2 items-center">
                    {/* Recent Submissions List */}
                    <div className="w-full max-w-5xl mb-8">
                         <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-300">Recent Test Scripts</h2>
                            <button 
                                onClick={() => fetchSubmissions()}
                                className="text-xs bg-gray-800 hover:bg-gray-700 text-blue-400 px-3 py-1 rounded border border-gray-700 transition-colors"
                            >
                                ↻ Refresh List
                            </button>
                        </div>
                        
                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                            {isLoadingList ? (
                                <div className="p-4 text-center text-gray-500 text-sm">Loading scripts...</div>
                            ) : submissions.length === 0 ? (
                                <div className="p-4 text-center text-gray-500 text-sm italic">No recent scripts found.</div>
                            ) : (
                                <table className="w-full text-left text-sm text-gray-400">
                                    <thead className="bg-gray-800 text-gray-500 uppercase font-bold text-xs sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2">Title</th>
                                            <th className="px-4 py-2">Date</th>
                                            <th className="px-4 py-2">Status</th>
                                            <th className="px-4 py-2 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {submissions.map((sub) => (
                                            <tr key={sub._id} className="hover:bg-gray-800/50 transition-colors group">
                                                <td className="px-4 py-3 font-medium text-white max-w-xs truncate">
                                                    {sub.title || sub.filename || "Untitled"}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {new Date(sub.created_at).toLocaleDateString()} <span className="text-xs text-gray-600 ml-1">{new Date(sub.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                                                        sub.status === 'completed' ? 'bg-green-900/30 text-green-400 border-green-900' :
                                                        sub.status === 'failed' ? 'bg-red-900/30 text-red-400 border-red-900' :
                                                        'bg-blue-900/30 text-blue-400 border-blue-900'
                                                    }`}>
                                                        {sub.status || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right flex justify-end gap-2">
                                                     <button 
                                                        onClick={(e) => { e.stopPropagation(); fetchLogs(sub._id); }}
                                                        className="text-gray-400 hover:text-purple-400 text-xs border border-gray-700 bg-gray-800 px-2 py-1 rounded"
                                                        title="View Processing Logs"
                                                    >
                                                        Logs
                                                    </button>
                                                    <button 
                                                        onClick={() => loadSubmission(sub._id)}
                                                        className="text-blue-400 hover:text-white hover:underline text-xs"
                                                    >
                                                        View Coverage &rarr;
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            placeholder="Recover ID..." 
                            value={manualId}
                            onChange={(e) => setManualId(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-32"
                        />
                        <button
                            onClick={handleManualRecover}
                            className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1 bg-gray-800 px-3 py-1 rounded border border-gray-700"
                        >
                            Recover
                        </button>
                    </div>

                     <button
                        onClick={clearAnalysis}
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 bg-gray-800 px-3 py-1 rounded border border-gray-700"
                    >
                        ⊗ Clear
                    </button>
                     <button
                        onClick={() => setIsFullWidth(!isFullWidth)}
                        className="text-xs text-gray-500 hover:text-white flex items-center gap-1 bg-gray-800 px-3 py-1 rounded border border-gray-700"
                    >
                        {isFullWidth ? '⇲ Standard' : '⇱ Full Width'}
                    </button>
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                        Script Analysis Lab
                    </h1>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        Experiment with different prompts and models to optimize your script analysis pipeline.
                    </p>
                </div>
                
                <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full space-y-8">
                    

                    {/* Upload Section */}
                    <div className="border border-gray-600 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">1. Upload Script (PDF)</h2>
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                            <input 
                                type="file" 
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-gray-400
                                  file:mr-4 file:py-2 file:px-4
                                  file:rounded-full file:border-0
                                  file:text-sm file:font-semibold
                                  file:bg-blue-600 file:text-white
                                  hover:file:bg-blue-700
                                "
                            />
                            <button 
                                onClick={handleUpload}
                                disabled={!file}
                                className={`px-6 py-2 rounded font-bold transition-colors ${
                                    file && !isLoading ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </span>
                                ) : (
                                    'Analyze Script'
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Output Section */}
                    {analysis.synopsis || analysis.scenes ? (
                         <div className="space-y-8">
                            
                            {/* Section A: Title & Logline */}
                            {(analysis.title || analysis.logline) && (
                                <div className="border-l-4 border-blue-500 pl-6 py-2">
                                    <h1 className="text-4xl font-bold text-white mb-2">{analysis.title}</h1>
                                    <p className="text-xl text-blue-200 italic leading-relaxed">"{analysis.logline}"</p>
                                </div>
                            )}

                            {/* Section B: Short Summary */}
                            {analysis.shortSummary && (
                                <div className="bg-gray-900/50 p-6 rounded-lg border border-gray-700">
                                    <h3 className="text-lg font-bold text-gray-400 uppercase tracking-widest mb-3">Executive Summary</h3>
                                    <p className="text-gray-300 leading-relaxed text-lg">
                                        {analysis.shortSummary}
                                    </p>
                                </div>
                            )}

                            {/* Section C: Long Summary */}
                            {(analysis.longSummary || analysis.synopsis) && (
                                <div className="border border-purple-800 p-8 rounded-lg bg-gray-900 shadow-xl">
                                    <h2 className="text-2xl font-serif text-purple-300 mb-6 border-b border-purple-800 pb-2">Narrative Synopsis</h2>
                                    <div className="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed whitespace-pre-wrap font-serif">
                                        {analysis.longSummary || analysis.synopsis}
                                    </div>
                                    <div className="mt-4 text-right text-gray-500 text-sm font-mono border-t border-purple-900/50 pt-2">
                                        Word Count: {(analysis.longSummary || analysis.synopsis).trim().split(/\s+/).length}
                                    </div>
                                </div>
                            )}

                            {/* Raw Parsed Text Accordion */}
                            <details className="border border-gray-700 rounded-lg bg-gray-800 p-4">
                                <summary className="cursor-pointer text-gray-400 font-semibold hover:text-white flex items-center gap-2">
                                    <span>📄 View Raw Extracted Text</span>
                                </summary>
                                <div className="mt-4 bg-gray-900 p-4 rounded max-h-[500px] overflow-y-auto whitespace-pre-wrap font-mono text-xs text-gray-500">
                                    {analysis.parsedText}
                                </div>
                            </details>

                            {/* Debug JSON Output: Parallel Extraction (GPT-5 Mini) */}
                            {/* Debug JSON Output: Parallel Extraction (GPT-5 Mini) */}
                             <details className="border border-yellow-900/50 p-4 rounded-lg bg-gray-900/30">
                                <summary className="cursor-pointer text-yellow-500 font-semibold hover:text-yellow-400 flex items-center gap-2">
                                    <span>🤖 Raw Data: Chunk Extraction (GPT-5 Mini)</span>
                                </summary>
                                <div className="mt-4">
                                    <p className="text-gray-400 mb-2 text-xs">Output from parallel API calls processing script chunks.</p>
                                    <div className="bg-black p-4 rounded-lg overflow-x-auto border border-gray-700">
                                        <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
                                            {JSON.stringify(analysis.debugChunkResults, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </details>

                            {/* Debug JSON Output: Final Synopsis (GPT-5.2 High) */}
                            <details className="border border-red-900/50 p-4 rounded-lg bg-gray-900/30">
                                <summary className="cursor-pointer text-red-500 font-semibold hover:text-red-400 flex items-center gap-2">
                                    <span>🧠 Raw Data: Final Synopsis (GPT-5.2 High)</span>
                                </summary>
                                <div className="mt-4">
                                    <p className="text-gray-400 mb-2 text-xs">Output from the final reasoning model merging all scenes.</p>
                                    <div className="bg-black p-4 rounded-lg overflow-x-auto border border-gray-700">
                                        <pre className="text-sm text-pink-400 font-mono whitespace-pre-wrap">
                                            {JSON.stringify(analysis.debugFinalReport, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </details>
                        </div>
                    ) : (
                         <div className="border border-gray-600 p-12 rounded-lg text-center text-gray-400 italic">
                            Upload a script to generate the Synopsis.
                        </div>
                    )}
                </div>

                <div className="border border-gray-600 p-4 rounded mt-8 w-full">
                    <h2 className="text-xl font-semibold mb-2">Debug Info</h2>
                    <p><strong>Email:</strong> {userDetails?.email || 'None'}</p>
                    <p><strong>Environment:</strong> {process.env.NODE_ENV}</p>
                </div>
            </div>
        </PageContainer>
    );
};

export default HiddenTestPage;
